import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { ConfigService } from '../config/config.service';
import { AuthService } from '../auth/auth.service';
import { environment } from '../../../environments/environment';
import { BrowserStorage } from '../../models/browserStorage';

@Injectable({
	providedIn: 'root',
})
export class SessionContextService {
	private courseCtx: {
		courseSessionId: string;
		categoryId: string;
		courseId: string;
	} | null = null;

	private chapterCtx: {
		chapterSessionId: string;
		chapterId: string;
	} | null = null;

	constructor(
		private configService: ConfigService,
		private authService: AuthService,
		private api: ApiService,
	) {}

	private get isActive(): boolean {
		return this.configService.isFeatureFlagEnabled('activityTrackingFeatureFlag', [this.authService.auth?.user?.id]);
	}

	get courseSessionId(): string | null {
		return this.courseCtx?.courseSessionId ?? null;
	}
	get chapterSessionId(): string | null {
		return this.chapterCtx?.chapterSessionId ?? null;
	}
	get categoryId(): string | null {
		return this.courseCtx?.categoryId ?? null;
	}
	get courseId(): string | null {
		return this.courseCtx?.courseId ?? null;
	}
	get chapterId(): string | null {
		return this.chapterCtx?.chapterId ?? null;
	}

	async enterCourse(categoryId: string, courseId: string): Promise<void> {
		if (!this.isActive) {
			return;
		}

		if (this.courseCtx) {
			this.leaveCourse();
		}

		this.courseCtx = {
			courseSessionId: crypto.randomUUID(),
			categoryId,
			courseId,
		};

		try {
			await this.api.post('/tracker/course/start', {
				courseSessionId: this.courseCtx.courseSessionId,
				categoryId,
				courseId,
				startedAt: Date.now(),
			});
		} catch (err) {
			console.error('Failed to start course session', err);
		}
	}

	leaveCourse(): void {
		if (!this.isActive || !this.courseCtx) {
			return;
		}

		this.leaveChapter();

		const payload = {
			courseSessionId: this.courseCtx.courseSessionId,
			endedAt: Date.now(),
		};

		fetch(`${environment.API_URL}/tracker/course/end`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': BrowserStorage.get('userToken')
			},
			body: JSON.stringify(payload),
			keepalive: true
		});

		// navigator.sendBeacon(
		// 	`${environment.API_URL}/tracker/course/end`,
		// 	new Blob([JSON.stringify(payload)], { type: 'application/json' }),
		// );

		this.courseCtx = null;
	}

	async enterChapter(chapterId: string): Promise<void> {
		if (!this.isActive || !this.courseCtx) {
			return;
		}

		if (this.chapterCtx) {
			this.leaveChapter();
		}

		this.chapterCtx = {
			chapterSessionId: crypto.randomUUID(),
			chapterId,
		};

		try {
			await this.api.post('/tracker/chapter/start', {
				chapterSessionId: this.chapterCtx.chapterSessionId,
				courseSessionId: this.courseCtx.courseSessionId,
				categoryId: this.courseCtx.categoryId,
				courseId: this.courseCtx.courseId,
				chapterId,
				startedAt: Date.now(),
			});
		} catch (err) {
			console.error('Failed to start chapter session', err);
		}
	}

	leaveChapter(): void {
		if (!this.isActive || !this.chapterCtx) {
			return;
		}

		const payload = {
			chapterSessionId: this.chapterCtx.chapterSessionId,
			endedAt: Date.now(),
		};

		fetch(`${environment.API_URL}/tracker/chapter/end`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': BrowserStorage.get('userToken')
			},
			body: JSON.stringify(payload),
			keepalive: true
		});

		// navigator.sendBeacon(
		// 	`${environment.API_URL}/tracker/chapter/end`,
		// 	new Blob([JSON.stringify(payload)], { type: 'application/json' }),
		// );

		this.chapterCtx = null;
	}
}
